import { Outlet, Link } from 'react-router'
import { Sidebar } from './Sidebar'
import { MobileBottomNav } from './MobileBottomNav'
import { NotificationBell } from './NotificationBell'
import { CommandPalette } from './CommandPalette'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { usePushTapRouting } from '@/hooks/usePushTapRouting'
import { ShortcutsHelp } from './ShortcutsHelp'
import { AskRizePanel, AskRizeButton } from './AskRizePanel'
import { UpdateAvailableBanner } from './UpdateAvailableBanner'
import { Bot } from 'lucide-react'
import { useEffect } from 'react'
import { useAuth } from '@/lib/auth'
import { applyBrandTheme, readBrandColor } from '@/lib/brandTheme'

export function AppLayout() {
  useKeyboardShortcuts()
  // RIZ132: a tapped push alert opens its page. The page routes itself (an iPhone ignores the url a tap asks
  // for), so this runs once, here, where every signed-in page renders and the bell is not doubled.
  usePushTapRouting()

  // ── THE BROKERAGE'S COLOUR, IN THE APP THEY WORK IN (tracker row 41) ────────────────
  //
  // Rize set a brand colour and reported that only the client portal changed. It had, and
  // this is the half that never happened: `--color-primary` is what every `bg-primary` in
  // the app resolves through at paint time, so setting it here re-themes the whole app
  // without touching a single component.
  //
  // Mounted on the AUTHENTICATED layout deliberately. The public pages — sign-in, DSAR,
  // the OAuth landing — have no organisation behind them and must stay on the platform's
  // own colour; painting those in a brokerage's would claim the product is theirs.
  //
  // The cleanup is not boilerplate. These are inline properties on the root element and
  // they outlive a React unmount, so signing out, or switching to an org with no brand,
  // has to actively clear them — otherwise the previous brokerage's colour follows the
  // user into the next one.
  const { activeOrg } = useAuth()
  const brandColor = readBrandColor(activeOrg?.organizations?.brand)
  useEffect(() => {
    applyBrandTheme(brandColor, document.documentElement)
    return () => applyBrandTheme(null, document.documentElement)
  }, [brandColor])

  return (
    <>
      {/* Skip link — first focusable element in the DOM */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <div className="flex h-[100dvh] overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <Sidebar />
        </div>

        {/* Main content area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Mobile header */}
          <header className="flex items-center justify-between border-b border-border px-4 py-3 md:hidden">
            <Link
              to="/"
              aria-label="Rize OS — go to Dashboard"
              className="rounded text-lg font-bold tracking-[-0.04em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              rize<span className="text-primary" aria-hidden="true">.</span>
            </Link>
            <div className="flex items-center gap-1">
              <AskRizeButton />
              <NotificationBell />
            </div>
          </header>

          {/* SW update banner (F3) — hidden until a new build is waiting */}
          <UpdateAvailableBanner />

          {/* Idaho HB 127 AI Disclosure */}
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="flex items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-1.5 text-xs text-muted-foreground"
          >
            <Bot className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span>You are interacting with an AI system, not a human being.</span>
          </div>

          <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto pb-16 md:pb-0 focus:outline-none">
            <Outlet />
          </main>
        </div>

        {/* Mobile bottom nav */}
        <MobileBottomNav />

        <CommandPalette />
        <ShortcutsHelp />
        <AskRizePanel />
      </div>
    </>
  )
}
